import React, { useState } from 'react';
import { Plus, Edit2, Trash2, X, QrCode, ExternalLink, Copy, Check, Store, UtensilsCrossed, Martini, Coffee, CakeSlice, Wine, Pizza, Sandwich, Fish, Soup, Beer, BoxIcon } from 'lucide-react';
import { useOutlets, Outlet } from './OutletsContext';
import { Branch } from './BranchesContext';
import { useMenus } from './MenusContext';
import { Toggle } from './Toggle';
const COLOR_SWATCHES = ['orange', 'blue', 'emerald', 'purple', 'pink', 'amber', 'rose', 'indigo'];
// Maps the outlet's stored icon key to a real lucide icon. Falls back to a
// generic store icon for unknown keys.
const ICON_OPTIONS: {
  key: string;
  Icon: BoxIcon;
}[] = [{
  key: 'utensils',
  Icon: UtensilsCrossed
}, {
  key: 'martini',
  Icon: Martini
}, {
  key: 'wine',
  Icon: Wine
}, {
  key: 'beer',
  Icon: Beer
}, {
  key: 'coffee',
  Icon: Coffee
}, {
  key: 'cake',
  Icon: CakeSlice
}, {
  key: 'pizza',
  Icon: Pizza
}, {
  key: 'sandwich',
  Icon: Sandwich
}, {
  key: 'fish',
  Icon: Fish
}, {
  key: 'soup',
  Icon: Soup
}];
function iconForKey(key?: string): BoxIcon {
  const found = ICON_OPTIONS.find(o => o.key === key);
  return found ? found.Icon : Store;
}
function OutletGlyph({
  outlet,
  size = 20
}: {
  outlet: Pick<Outlet, 'icon' | 'name'>;
  size?: number;
}) {
  // Emoji (or any non-keyed short glyph): render as text. Otherwise render the
  // mapped lucide icon. Final fallback: the capitalized first letter.
  if (outlet.icon && !ICON_OPTIONS.some(o => o.key === outlet.icon)) {
    const isShortGlyph = [...outlet.icon].length <= 2;
    if (isShortGlyph) {
      return <span className="text-lg leading-none" data-id="element-673">{outlet.icon}</span>;
    }
  }
  if (outlet.icon) {
    const Icon = iconForKey(outlet.icon);
    return <Icon size={size} strokeWidth={2} data-id="element-674" />;
  }
  return <span className="text-base font-bold leading-none" data-id="element-675">
      {outlet.name.charAt(0).toUpperCase()}
    </span>;
}
export function BranchOutletsSection({
  branch
}: {
  branch: Branch;
}) {
  const {
    getOutletsForBranch,
    addOutlet,
    updateOutlet,
    deleteOutlet
  } = useOutlets();
  const {
    menus
  } = useMenus();
  const outlets = getOutletsForBranch(branch.id);
  const [editingOutletId, setEditingOutletId] = useState<string | null>(null);
  const [showQRFor, setShowQRFor] = useState<Outlet | null>(null);
  const handleAdd = () => {
    const id = addOutlet(branch.id);
    setEditingOutletId(id);
  };
  return <div className="space-y-5" data-id="element-676">
      <div className="flex items-center justify-between gap-4" data-id="element-677">
        <div className="flex items-center gap-2.5" data-id="element-678">
          <h3 className="text-sm font-bold text-gray-900" data-id="element-679">Outlets</h3>
          {outlets.length > 0 && <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-gray-100 text-gray-600 text-[11px] font-bold tabular-nums" data-id="element-680">
              {outlets.length}
            </span>}
        </div>
        <button onClick={handleAdd} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold bg-orange-500 text-white hover:bg-orange-600 active:bg-orange-700 shadow-sm transition-colors" data-id="element-681">
          <Plus size={16} strokeWidth={2.5} data-id="element-682" />
          Add outlet
        </button>
      </div>

      {outlets.length === 0 ? <div className="flex flex-col items-center justify-center text-center py-12 px-6 rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50/60" data-id="element-683">
          <div className="w-12 h-12 rounded-xl bg-white border border-gray-200 flex items-center justify-center text-gray-400 mb-3 shadow-sm" data-id="element-684">
            <Store size={22} data-id="element-685" />
          </div>
          <p className="text-sm font-semibold text-gray-900" data-id="element-686">No outlets yet</p>
          <p className="text-xs text-gray-500 mt-1 max-w-xs" data-id="element-687">
            Add a restaurant concept — like a bar or a kitchen — to give this
            location its own menu and QR code.
          </p>
          <button onClick={handleAdd} className="mt-4 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold bg-gray-900 text-white hover:bg-gray-800 transition-colors" data-id="element-688">
            <Plus size={16} strokeWidth={2.5} data-id="element-689" />
            Add your first outlet
          </button>
        </div> : <div className="space-y-2.5" data-id="element-690">
          {outlets.map(outlet => editingOutletId === outlet.id ? <OutletEditor key={outlet.id} outlet={outlet} branch={branch} menus={menus} onSave={patch => {
        updateOutlet(outlet.id, patch);
        setEditingOutletId(null);
      }} onCancel={() => setEditingOutletId(null)} data-id="element-691" /> : <OutletRow key={outlet.id} outlet={outlet} onEdit={() => setEditingOutletId(outlet.id)} onDelete={() => {
        if (window.confirm(`Delete outlet "${outlet.name}"?`)) deleteOutlet(outlet.id);
      }} onShowQR={() => setShowQRFor(outlet)} data-id="element-692" />)}
        </div>}

      {showQRFor && <OutletQRModal outlet={showQRFor} onClose={() => setShowQRFor(null)} data-id="element-693" />}
    </div>;
}
function OutletRow({
  outlet,
  onEdit,
  onDelete,
  onShowQR
}: {
  outlet: Outlet;
  onEdit: () => void;
  onDelete: () => void;
  onShowQR: () => void;
}) {
  return <div className="flex items-center justify-between gap-3 p-4 rounded-xl border border-gray-200 bg-white" data-id="element-694">
      <div className="flex items-center gap-3 min-w-0" data-id="element-695">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center bg-${outlet.color}-100 text-${outlet.color}-600 flex-shrink-0`} data-id="element-696">
          <OutletGlyph outlet={outlet} size={20} data-id="element-697" />
        </div>
        <div className="min-w-0" data-id="element-698">
          <div className="flex items-center gap-2" data-id="element-699">
            <span className="font-bold text-gray-900 text-sm truncate" data-id="element-700">
              {outlet.name}
            </span>
            {!outlet.active && <span className="text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded bg-gray-100 text-gray-500" data-id="element-701">
                Inactive
              </span>}
          </div>
          <div className="text-xs text-gray-500 truncate" data-id="element-702">
            {outlet.description || 'No description'}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0" data-id="element-703">
        <button onClick={onShowQR} className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors" title="View QR & link" aria-label="View QR & link" data-id="element-704">
          <QrCode size={16} data-id="element-705" />
        </button>
        <button onClick={onEdit} className="p-2 rounded-lg text-gray-400 hover:text-orange-600 hover:bg-orange-50 transition-colors" title="Edit outlet" aria-label="Edit outlet" data-id="element-706">
          <Edit2 size={16} data-id="element-707" />
        </button>
        <button onClick={onDelete} className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="Delete outlet" aria-label="Delete outlet" data-id="element-708">
          <Trash2 size={16} data-id="element-709" />
        </button>
      </div>
    </div>;
}
function OutletEditor({
  outlet,
  branch,
  menus,
  onSave,
  onCancel
}: {
  outlet: Outlet;
  branch: Branch;
  menus: {
    id: string;
    name: string;
  }[];
  onSave: (patch: Partial<Outlet>) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(outlet.name);
  const [description, setDescription] = useState(outlet.description);
  const [color, setColor] = useState(outlet.color);
  const [icon, setIcon] = useState(outlet.icon || '');
  const [active, setActive] = useState(outlet.active);
  const [defaultMenuId, setDefaultMenuId] = useState(outlet.defaultMenuId);
  const [stationIds, setStationIds] = useState<string[]>(outlet.stationIds || []);
  const toggleStation = (id: string) => {
    setStationIds(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };
  return <div className="p-4 rounded-xl border-2 border-orange-200 bg-orange-50/40 space-y-4" data-id="element-710">
      <label className="block" data-id="element-711">
        <span className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-712">
          Outlet name
        </span>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Skyline Bar" className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition" data-id="element-713" />
      </label>

      <div data-id="element-714">
        <span className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-715">
          Icon
        </span>
        <div className="flex flex-wrap gap-2" data-id="element-716">
          {ICON_OPTIONS.map(({
          key,
          Icon
        }) => {
          const on = icon === key;
          return <button key={key} type="button" onClick={() => setIcon(key)} className={`w-9 h-9 rounded-lg flex items-center justify-center border transition-colors ${on ? `bg-${color}-100 border-${color}-400 text-${color}-600` : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700'}`} aria-label={key} aria-pressed={on} data-id="element-717">
                <Icon size={17} strokeWidth={2} data-id="element-718" />
              </button>;
        })}
        </div>
      </div>

      <label className="block" data-id="element-719">
        <span className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-720">
          Description
        </span>
        <input value={description} onChange={e => setDescription(e.target.value)} placeholder="Short tagline shown to customers" className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition" data-id="element-721" />
      </label>

      <div data-id="element-722">
        <span className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-723">
          Brand color
        </span>
        <div className="flex flex-wrap gap-2" data-id="element-724">
          {COLOR_SWATCHES.map(c => <button key={c} type="button" onClick={() => setColor(c)} className={`w-8 h-8 rounded-full bg-${c}-500 transition-transform ${color === c ? 'ring-2 ring-offset-2 ring-gray-900 scale-110' : 'hover:scale-105'}`} aria-label={`Brand color ${c}`} data-id="element-725" />)}
        </div>
      </div>

      <label className="block" data-id="element-726">
        <span className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-727">
          Default menu
        </span>
        <select value={defaultMenuId} onChange={e => setDefaultMenuId(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition" data-id="element-728">
          {menus.map(m => <option key={m.id} value={m.id} data-id="element-729">
              {m.name}
            </option>)}
        </select>
      </label>

      <div data-id="element-730">
        <span className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-731">
          Kitchen stations
        </span>
        <div className="flex flex-wrap gap-2" data-id="element-732">
          {(branch.stations || []).map(st => {
          const on = stationIds.includes(st.id);
          return <button key={st.id} type="button" onClick={() => toggleStation(st.id)} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${on ? 'bg-[#0f172a] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-733">
                {st.name}
              </button>;
        })}
          {(!branch.stations || branch.stations.length === 0) && <span className="text-xs text-gray-400" data-id="element-734">
              No stations configured for this branch.
            </span>}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-white border border-gray-200" data-id="element-735">
        <span className="text-sm font-semibold text-gray-900" data-id="element-736">Active</span>
        <Toggle checked={active} onChange={setActive} data-id="element-737" />
      </div>

      <div className="flex gap-2.5 pt-1" data-id="element-738">
        <button onClick={onCancel} className="flex-1 py-2.5 rounded-lg border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors" data-id="element-739">
          Cancel
        </button>
        <button onClick={() => onSave({
        name: name.trim() || 'Untitled outlet',
        description,
        color,
        icon: icon.trim(),
        active,
        defaultMenuId,
        stationIds
      })} className="flex-[1.5] py-2.5 rounded-lg bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 transition-colors" data-id="element-740">
          Save outlet
        </button>
      </div>
    </div>;
}
function OutletQRModal({
  outlet,
  onClose
}: {
  outlet: Outlet;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const link = `${origin}/menu?outlet=${outlet.id}`;
  const copy = () => {
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };
  return <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-741">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden" data-id="element-742">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100" data-id="element-743">
          <h3 className="font-bold text-gray-900 flex items-center gap-2" data-id="element-744">
            <span className={`w-8 h-8 rounded-lg flex items-center justify-center bg-${outlet.color}-100 text-${outlet.color}-600 flex-shrink-0`} data-id="element-745">
              <OutletGlyph outlet={outlet} size={16} data-id="element-746" />
            </span>
            {outlet.name}
          </h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700" aria-label="Close" data-id="element-747">
            <X size={20} data-id="element-748" />
          </button>
        </div>

        <div className="p-5 space-y-4" data-id="element-749">
          <div className={`mx-auto w-44 h-44 rounded-xl border-2 border-dashed border-${outlet.color}-300 bg-${outlet.color}-50 flex flex-col items-center justify-center text-${outlet.color}-600 gap-2`} data-id="element-750">
            <QrCode size={56} data-id="element-751" />
            <span className="text-[11px] font-semibold uppercase tracking-wide" data-id="element-752">
              Scan to order
            </span>
          </div>

          <div data-id="element-753">
            <span className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-754">
              Customer link
            </span>
            <div className="flex items-center gap-2" data-id="element-755">
              <input readOnly value={link} className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-xs bg-gray-50 text-gray-700 truncate" data-id="element-756" />
              <button onClick={copy} className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors flex-shrink-0" title="Copy link" aria-label="Copy link" data-id="element-757">
                {copied ? <Check size={16} className="text-emerald-600" data-id="element-758" /> : <Copy size={16} data-id="element-759" />}
              </button>
            </div>
          </div>

          <a href={link} target="_blank" rel="noopener noreferrer" className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[#0f172a] text-white text-sm font-semibold hover:bg-[#1e293b] transition-colors" data-id="element-760">
            <ExternalLink size={15} data-id="element-761" />
            Open outlet menu
          </a>
        </div>
      </div>
    </div>;
}